import {
  Box,
  BoxProps,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Switch,
  SwitchProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { getFeatureDisabledKey } from "@picovico/utils";
import { useId, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useWebFeature } from "@/hooks/useWebFeature";
import { useWhatsAppMe } from "@/hooks/useWhatsApp";
import { useAppDispatch } from "store/hooks";
import {
  useUpdateWhatsappMeMutation,
  whatsappApi,
} from "store/services/whatsapp";
import {
  offersSaveErrorMessage,
  showsWhatsappOffersToggle,
  whatsappOffersHint,
} from "utils/whatsapp";
import { WHATSAPP_GREEN } from "./WhatsAppOptIn";

export const WHATSAPP_OFFERS_TOGGLE_LABEL = "Offers on WhatsApp";
const SAVE_FAILED = "Could not save your choice. Please try again.";

export { whatsappOffersHint };

export interface WhatsAppOffersToggleProps {
  containerProps?: BoxProps;
  switchProps?: SwitchProps;
  hintProps?: TextProps;
  errorProps?: TextProps;
}

/**
 * The profile's "Offers on WhatsApp" switch (claude/design/whatsapp-messaging/
 * 04 §3, as pvapp built it): shown when the company has `whatsapp.promotions`
 * and `/whatsapp/me/` gives both `offers.available` and `offers.number` (the
 * verified mobile, which the line names). It saves as it is switched
 * (`PATCH /whatsapp/me/ {offers_opt_in}`, never a `number`: offers only go to
 * the verified mobile), apart from the profile form. A refusal shows under
 * it: the server's sentence for a 400, "Too many changes. Try again in N
 * seconds." for the 429 `THROTTLED`. Switching off is the customer's opt-out
 * (D19).
 */
function WhatsAppOffersToggle({
  containerProps,
  switchProps,
  hintProps,
  errorProps,
}: WhatsAppOffersToggleProps) {
  const switchId = useId();
  const dispatch = useAppDispatch();
  const promotionsFlag = useWebFeature("whatsapp.promotions");
  // No `GET /whatsapp/me/` for a company without promotions.
  const me = useWhatsAppMe({ skip: !promotionsFlag });
  const [updateMe] = useUpdateWhatsappMeMutation();
  // The value being saved, shown meanwhile.
  const [pending, setPending] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!me || !showsWhatsappOffersToggle({ promotionsFlag, me })) return null;

  const save = async (value: boolean) => {
    setPending(value);
    setError(null);
    try {
      await updateMe({ offers_opt_in: value }).unwrap();
    } catch (failure) {
      setError(offersSaveErrorMessage(failure, SAVE_FAILED));
      // Switched off since the page loaded: read again, which hides the switch.
      if (getFeatureDisabledKey(failure)) {
        dispatch(whatsappApi.util.invalidateTags(["WhatsAppMe"]));
      }
    } finally {
      setPending(null);
    }
  };

  const on = pending ?? me.offers.opted_in;

  return (
    <Box
      data-testid="whatsapp-offers-section"
      w="full"
      mt="0.5rem"
      pt="1rem"
      borderTopWidth="1px"
      {...containerProps}
    >
      <FormControl
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="1rem"
      >
        <Flex align="center" gap="0.5rem">
          <Icon
            as={FaWhatsapp}
            boxSize="1.25rem"
            color={WHATSAPP_GREEN}
            aria-hidden
          />
          <FormLabel htmlFor={switchId} mb="0" fontWeight={600}>
            {WHATSAPP_OFFERS_TOGGLE_LABEL}
          </FormLabel>
        </Flex>
        <Switch
          id={switchId}
          isChecked={on}
          isDisabled={pending !== null}
          onChange={(event) => save(event.target.checked)}
          {...switchProps}
        />
      </FormControl>
      <Text fontSize="sm" opacity={0.75} mt="0.25rem" {...hintProps}>
        {whatsappOffersHint(me.offers.number)}
      </Text>
      {error && (
        <Text
          role="alert"
          fontSize="sm"
          color="red.500"
          mt="0.25rem"
          {...errorProps}
        >
          {error}
        </Text>
      )}
    </Box>
  );
}

export default WhatsAppOffersToggle;
